// ============================================================================
// BANK ACCOUNT HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { bankAccountService } from 'api/services/bankAccountService';
import type {
  BankAccount,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
  BankAccountSearchRequest,
  ID,
} from 'api/types';

export const useBankAccounts = (params?: BankAccountSearchRequest) => {
  const [data, setData] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await bankAccountService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, pagination, refetch: fetchAll };
};

export const useBankAccount = (id: ID) => {
  const [data, setData] = useState<BankAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await bankAccountService.getById(id);
      setData(response.data);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

export const useCreateBankAccount = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createBankAccount = async (
    data: CreateBankAccountRequest,
    options?: { onSuccess?: (item: BankAccount) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await bankAccountService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createBankAccount, loading, error };
};

export const useUpdateBankAccount = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateBankAccount = async (
    id: ID,
    data: UpdateBankAccountRequest,
    options?: { onSuccess?: (item: BankAccount) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await bankAccountService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateBankAccount, loading, error };
};

export const useDeleteBankAccount = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteBankAccount = async (
    id: ID,
    rowVersion: number,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      await bankAccountService.delete(id, rowVersion);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteBankAccount, loading, error };
};
